/**
 * assemblyTree.js — pure hierarchy logic for the synthetic CAD assemblies.
 *
 * The GLB node graph is flat-ish (root -> A1..A9 -> leaves) and occt-import-js
 * flattens nested assemblies on read, so the tree is reconstructed from the id
 * convention, never from the importer's node graph.
 *
 *   A<n>            assembly       A3_ENERGY_STORAGE
 *   S<n>_<m>        sub-assembly   S3_1_HV_BATTERY_PACK
 *   U<n>_<m>_<k>    unit           U3_1_3_CELL_MODULE_04
 *
 * RAGGED parent rule (implemented exactly):
 *   A<n>          -> root
 *   S<n>_<m>      -> the A<n> node
 *   U<n>_<m>_<k>  -> S<n>_<m> if a node with that exact prefix exists,
 *                    otherwise the A<n> node.
 *
 * Everything here is pure, total and free of three.js / React. It never throws
 * on malformed input: problems are surfaced in `tree.diagnostics`.
 *
 * No TypeScript in this project — plain JS + JSDoc.
 */

export const LEVEL_ASSEMBLY = 'assembly';
export const LEVEL_SUBASSEMBLY = 'subassembly';
export const LEVEL_UNIT = 'unit';
export const LEVEL_UNKNOWN = 'unknown';

/** Tokens that must survive title-casing verbatim. */
const ACRONYMS = new Set([
  'HV', 'LV', 'EV', 'BMS', 'EVCU', 'ECU', 'OBC', 'PDU', 'ABS', 'DC', 'AC',
  'DCDC', 'HVAC', 'VCU', 'MCU', 'TPMS', 'CAN', 'LFP', 'PMSM', 'SOC', 'SOH',
  'L', 'R', 'F', 'ID', 'RPM', 'GPS', 'USB', 'IP', 'KW', 'KWH', 'NM', 'V', 'A',
]);

const isDigits = (s) => s.length > 0 && /^[0-9]+$/.test(s);

/**
 * Spellings of the three levels that appear in manifests, folded onto the
 * canonical LEVEL_* constants every consumer compares against. The generator
 * writes "sub-assembly"; the UI keys off "subassembly".
 */
const LEVEL_ALIASES = {
  assembly: LEVEL_ASSEMBLY,
  subassembly: LEVEL_SUBASSEMBLY,
  'sub-assembly': LEVEL_SUBASSEMBLY,
  sub_assembly: LEVEL_SUBASSEMBLY,
  'sub assembly': LEVEL_SUBASSEMBLY,
  unit: LEVEL_UNIT,
  unknown: LEVEL_UNKNOWN,
};

/** Canonicalise a manifest `level` string; unknown spellings pass through. */
export function canonicalLevel(level) {
  if (typeof level !== 'string' || !level) return null;
  return LEVEL_ALIASES[level.trim().toLowerCase()] ?? level;
}

/**
 * Parse an assembly id into its structural coordinates.
 *
 * @param {string} id
 * @returns {{id:string, level:string, a:(number|null), s:(number|null),
 *            u:(number|null), rest:string[], valid:boolean}}
 *
 * `rest` is the descriptive remainder (already split on "_"). For a unit whose
 * third segment is not numeric (e.g. U1_1_L_LONGITUDINAL_RAIL) that segment is
 * treated as part of the description, not as the unit index, so the L/R
 * distinction survives into the display name and `u` stays null.
 */
export function parseId(id) {
  const empty = { id: String(id ?? ''), level: LEVEL_UNKNOWN, a: null, s: null, u: null, rest: [], valid: false };
  if (typeof id !== 'string' || id.length === 0) return empty;

  const parts = id.split('_');
  const head = parts[0];
  const kind = head[0];
  const aStr = head.slice(1);
  if (!isDigits(aStr)) return empty;
  const a = Number(aStr);

  if (kind === 'A') {
    return { id, level: LEVEL_ASSEMBLY, a, s: null, u: null, rest: parts.slice(1), valid: true };
  }
  if (kind === 'S') {
    if (!isDigits(parts[1] ?? '')) return empty;
    return { id, level: LEVEL_SUBASSEMBLY, a, s: Number(parts[1]), u: null, rest: parts.slice(2), valid: true };
  }
  if (kind === 'U') {
    if (!isDigits(parts[1] ?? '')) return empty;
    const s = Number(parts[1]);
    const third = parts[2] ?? '';
    if (isDigits(third)) {
      return { id, level: LEVEL_UNIT, a, s, u: Number(third), rest: parts.slice(3), valid: true };
    }
    return { id, level: LEVEL_UNIT, a, s, u: null, rest: parts.slice(2), valid: true };
  }
  return empty;
}

/** "A3_ENERGY_STORAGE" -> "A3"; "U3_1_3_CELL_MODULE_04" -> "U3_1_3". */
function idPrefixOf(parsed) {
  if (parsed.level === LEVEL_ASSEMBLY) return `A${parsed.a}`;
  if (parsed.level === LEVEL_SUBASSEMBLY) return `S${parsed.a}_${parsed.s}`;
  if (parsed.level === LEVEL_UNIT) {
    return parsed.u === null ? `U${parsed.a}_${parsed.s}` : `U${parsed.a}_${parsed.s}_${parsed.u}`;
  }
  return '';
}

function titleToken(tok) {
  if (tok.length === 0) return tok;
  if (ACRONYMS.has(tok)) return tok;
  if (/[0-9]/.test(tok) && /^[A-Z0-9]+$/.test(tok)) return tok; // 3KW, 01, 04
  return tok[0].toUpperCase() + tok.slice(1).toLowerCase();
}

/**
 * Human label for an id: strip the structural prefix and title-case the rest.
 *   "U3_1_3_CELL_MODULE_04" -> "Cell Module 04"
 *   "A3_ENERGY_STORAGE"     -> "Energy Storage"
 *   "U1_1_L_LONGITUDINAL_RAIL" -> "L Longitudinal Rail"
 * @param {string} id
 * @returns {string}
 */
export function displayName(id) {
  const parsed = parseId(id);
  const tokens = parsed.valid ? parsed.rest : String(id ?? '').split('_');
  const label = tokens.filter(Boolean).map(titleToken).join(' ');
  return label || String(id ?? '');
}

/**
 * Derive the parent id purely from the id convention, given the set of ids
 * that actually exist. Returns null for roots / unresolvable.
 * @param {string} id
 * @param {Set<string>} presentIds
 * @param {Map<string,string[]>} sPrefixIndex  "S3_1" -> node ids carrying that prefix
 * @param {Map<string,string>} aPrefixIndex    "A3"   -> actual node id
 * @param {(d:object)=>void} onDiag
 */
function derivedParent(id, presentIds, sPrefixIndex, aPrefixIndex, onDiag) {
  const p = parseId(id);
  if (!p.valid) return null;
  if (p.level === LEVEL_ASSEMBLY) return null;
  const aId = aPrefixIndex.get(`A${p.a}`) ?? null;
  if (p.level === LEVEL_SUBASSEMBLY) return aId;
  // unit: prefer the S<n>_<m> node when one exists, else fall back to A<n>
  const prefix = `S${p.a}_${p.s}`;
  const candidates = (sPrefixIndex.get(prefix) ?? []).filter((c) => c !== id && presentIds.has(c));
  if (candidates.length > 1) {
    onDiag({
      type: 'ambiguous_subassembly_prefix',
      id,
      message: `prefix "${prefix}" is carried by ${candidates.length} nodes; attaching "${id}" to "${candidates[0]}"`,
      detail: candidates,
    });
  }
  if (candidates.length > 0) return candidates[0];
  return aId;
}

const LEVEL_RANK = {
  [LEVEL_ASSEMBLY]: 0,
  [LEVEL_SUBASSEMBLY]: 1,
  [LEVEL_UNIT]: 2,
  [LEVEL_UNKNOWN]: 3,
};

/**
 * Convert either manifest shape into a flat node array.
 * Accepts the UI manifest ({nodes:[...]}) and the raw generator manifest
 * ({assemblies:[{assembly, parts:[...]}]}). Also accepts a bare array.
 * @param {object|Array} manifest
 * @returns {Array<object>}
 */
export function nodesFromManifest(manifest) {
  if (Array.isArray(manifest)) return manifest.slice();
  if (!manifest || typeof manifest !== 'object') return [];
  if (Array.isArray(manifest.nodes)) return manifest.nodes.slice();
  if (Array.isArray(manifest.assemblies)) {
    const out = [];
    const seen = new Set();
    for (const asm of manifest.assemblies) {
      if (!asm || typeof asm !== 'object') continue;
      const aId = asm.assembly;
      if (typeof aId === 'string' && !seen.has(aId)) {
        seen.add(aId);
        out.push({ id: aId });
      }
      for (const partId of asm.parts ?? []) {
        if (typeof partId === 'string' && !seen.has(partId)) {
          seen.add(partId);
          out.push({ id: partId });
        }
      }
    }
    return out;
  }
  return [];
}

/**
 * Build the assembly tree.
 *
 * @param {Array<object>|object} input  node array, or a manifest to derive one from
 * @returns {{
 *   roots: string[],
 *   byId: Map<string, object>,
 *   order: string[],                       // preorder DFS over the whole forest
 *   childrenOf: (id:string) => string[],
 *   ancestorsOf: (id:string) => string[],  // nearest parent first, root last
 *   descendantsOf: (id:string) => string[],// preorder, excludes `id`
 *   parentOf: (id:string) => (string|null),
 *   depthOf: (id:string) => number,        // roots are 0; -1 if unknown id
 *   nodes: object[],
 *   diagnostics: Array<{type:string, id:string, message:string, detail?:*}>
 * }}
 *
 * Node objects are normalised copies carrying: id, name, level, parent,
 * children, depth, explode (if supplied), kg_ids, plus every original field.
 *
 * Pure and total: unknown ids return empty arrays, nothing throws.
 */
export function buildTree(input) {
  const raw = nodesFromManifest(input);
  /** @type {Array<{type:string,id:string,message:string,detail?:*}>} */
  const diagnostics = [];

  const byId = new Map();
  const sPrefixIndex = new Map();
  const aPrefixIndex = new Map();
  const presentIds = new Set();

  for (const src of raw) {
    if (!src || typeof src !== 'object' || typeof src.id !== 'string' || src.id === '') {
      diagnostics.push({ type: 'invalid_node', id: '', message: 'node without a string id was skipped', detail: src });
      continue;
    }
    if (byId.has(src.id)) {
      diagnostics.push({ type: 'duplicate_id', id: src.id, message: `duplicate node id "${src.id}" ignored` });
      continue;
    }
    const parsed = parseId(src.id);
    if (!parsed.valid) {
      diagnostics.push({ type: 'unparseable_id', id: src.id, message: `id "${src.id}" does not match the A/S/U convention` });
    }
    // Capture the declared parent BEFORE normalisation overwrites `parent`.
    const declaredParent = (typeof src.parent === 'string' && src.parent) ? src.parent
      : ((typeof src.parentId === 'string' && src.parentId) ? src.parentId : null);
    const node = {
      ...src,
      id: src.id,
      name: typeof src.name === 'string' && src.name ? src.name : displayName(src.id),
      level: canonicalLevel(src.level) ?? parsed.level,
      kg_ids: Array.isArray(src.kg_ids) ? src.kg_ids : [],
      parsed,
      declaredParent,
      parent: null,
      children: [],
      depth: 0,
    };
    byId.set(node.id, node);
    presentIds.add(node.id);

    const prefix = idPrefixOf(parsed);
    if (parsed.level === LEVEL_ASSEMBLY && !aPrefixIndex.has(prefix)) aPrefixIndex.set(prefix, node.id);
    if (parsed.level === LEVEL_SUBASSEMBLY) {
      // Duplicate S-prefixes are normal (S7_1_REAR_L_WHEEL / S7_1_FRONT_L_WHEEL).
      // Only report it if a unit ever has to disambiguate between them.
      const list = sPrefixIndex.get(prefix);
      if (list) list.push(node.id); else sPrefixIndex.set(prefix, [node.id]);
    }
  }

  // --- parent resolution -------------------------------------------------
  for (const node of byId.values()) {
    const explicit = node.declaredParent;
    let parent = null;

    if (explicit) {
      if (explicit === node.id) {
        diagnostics.push({ type: 'self_parent', id: node.id, message: `"${node.id}" declares itself as its parent; treated as a root` });
      } else if (byId.has(explicit)) {
        parent = explicit;
      } else {
        diagnostics.push({
          type: 'orphan',
          id: node.id,
          message: `declared parent "${explicit}" does not exist; falling back to the id convention`,
          detail: explicit,
        });
        parent = derivedParent(node.id, presentIds, sPrefixIndex, aPrefixIndex, (d) => diagnostics.push(d));
      }
    } else {
      parent = derivedParent(node.id, presentIds, sPrefixIndex, aPrefixIndex, (d) => diagnostics.push(d));
    }

    if (parent === null && node.parsed.valid && node.parsed.level !== LEVEL_ASSEMBLY) {
      diagnostics.push({
        type: 'missing_ancestor',
        id: node.id,
        message: `no A${node.parsed.a} node present for "${node.id}"; promoted to a root`,
      });
    }
    node.parent = parent;
  }

  // --- cycle breaking ----------------------------------------------------
  // Only reachable via explicit parents; the id convention cannot cycle.
  for (const node of byId.values()) {
    const seen = new Set([node.id]);
    let cur = node.parent;
    while (cur !== null) {
      if (seen.has(cur)) {
        diagnostics.push({
          type: 'cycle',
          id: node.id,
          message: `parent chain from "${node.id}" cycles at "${cur}"; link broken, node promoted to a root`,
          detail: [...seen],
        });
        node.parent = null;
        break;
      }
      seen.add(cur);
      const next = byId.get(cur);
      if (!next) break;
      cur = next.parent;
    }
  }

  // --- child lists (stable: level rank, then original insertion order) ----
  const insertion = new Map();
  let i = 0;
  for (const id of byId.keys()) insertion.set(id, i++);

  const roots = [];
  for (const node of byId.values()) {
    if (node.parent === null) roots.push(node.id);
    else byId.get(node.parent).children.push(node.id);
  }
  const cmp = (x, y) => {
    const nx = byId.get(x); const ny = byId.get(y);
    const rx = LEVEL_RANK[nx.level] ?? 3; const ry = LEVEL_RANK[ny.level] ?? 3;
    if (rx !== ry) return rx - ry;
    return insertion.get(x) - insertion.get(y);
  };
  roots.sort(cmp);
  for (const node of byId.values()) node.children.sort(cmp);

  // --- depth + preorder --------------------------------------------------
  const order = [];
  const stack = [...roots].reverse();
  const visited = new Set();
  while (stack.length) {
    const id = stack.pop();
    if (visited.has(id)) continue;
    visited.add(id);
    order.push(id);
    const node = byId.get(id);
    node.depth = node.parent === null ? 0 : (byId.get(node.parent).depth + 1);
    for (let k = node.children.length - 1; k >= 0; k--) stack.push(node.children[k]);
  }

  const EMPTY = Object.freeze([]);
  const childrenOf = (id) => (byId.get(id)?.children ?? EMPTY);
  const parentOf = (id) => (byId.get(id)?.parent ?? null);
  const depthOf = (id) => (byId.has(id) ? byId.get(id).depth : -1);

  const ancestorsOf = (id) => {
    const out = [];
    if (!byId.has(id)) return out;
    const seen = new Set([id]);
    let cur = byId.get(id).parent;
    while (cur !== null && byId.has(cur) && !seen.has(cur)) {
      out.push(cur);
      seen.add(cur);
      cur = byId.get(cur).parent;
    }
    return out;
  };

  const descendantsOf = (id) => {
    const out = [];
    if (!byId.has(id)) return out;
    const st = [...byId.get(id).children].reverse();
    const seen = new Set([id]);
    while (st.length) {
      const cur = st.pop();
      if (seen.has(cur)) continue;
      seen.add(cur);
      out.push(cur);
      const kids = byId.get(cur)?.children ?? EMPTY;
      for (let k = kids.length - 1; k >= 0; k--) st.push(kids[k]);
    }
    return out;
  };

  return {
    roots,
    byId,
    order,
    nodes: order.map((id) => byId.get(id)),
    childrenOf,
    parentOf,
    ancestorsOf,
    descendantsOf,
    depthOf,
    diagnostics,
  };
}

export default buildTree;
